import { toast } from 'react-toastify';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { isEndorsementData } from '@ContractBuilder/types';
import type { DropApiOperation } from '@domain/schemas/api-patch-operation';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import type {
  SummaryBlockDelete,
  SummaryBlockHideFromPreview,
  SummaryBlockReorder,
  SummaryBlockShowInPreview,
  SummaryBlockUpdate,
  SummarySectionDelete,
  SummarySectionReorder,
} from '@domain/schemas/endorsement-summary.schema';
import {
  ENDORSEMENT_DETAILS_SENTINEL,
  summaryBlockCreateSchema,
  summaryBlockUpdateSchema,
} from '@domain/schemas/endorsement-summary.schema';
import { patchEndorsementSummary } from '@mutations/patchEndorsementSummary';
import { patchEntity } from '@mutations/patchEntity';
import { isSummarySection, reorderLayout } from '@root/helpers';
import { useMutation } from '@tanstack/react-query';
import { get } from 'lodash-es';

export function useEndorsementSummaryMutations() {
  const reloadSubmission = () => useEntityStore.getState().reloadSubmission();
  const highlightChangedBlock = (blockId?: string) => useEntityStore.getState().highlightChangedBlock(blockId);

  const createSummaryBlock = useMutation({
    mutationFn: async (payload: BlockEditFormState) => {
      const operation = { operation: ApiPatchOperation.CreateSummaryBlock } as const;
      const validationResult = summaryBlockCreateSchema.safeParse({ ...payload, ...operation });

      if (!validationResult.success) {
        toast.error('Unable to create summary block');
        throw new Error('Validation failed');
      }

      return patchEndorsementSummary({ ...validationResult.data, ...operation });
    },
    onSuccess: async (result) => {
      await reloadSubmission();
      if (result.id) highlightChangedBlock(result.id);
    },
  });

  const deleteSummaryBlock = useMutation({
    mutationFn: (payload: DropApiOperation<SummaryBlockDelete>) =>
      patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.DeleteSummaryBlock }),
    onSuccess: async () => {
      await reloadSubmission();
      toast.success('Removed block');
    },
  });

  const deleteSummarySection = useMutation({
    mutationFn: (payload: DropApiOperation<SummarySectionDelete>) =>
      patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.DeleteSummarySection }),
    onSuccess: reloadSubmission,
  });

  const updateSummaryBlock = useMutation({
    mutationFn: async (payload: DropApiOperation<SummaryBlockUpdate>) => {
      useEntityStore.setState({ loadingBlockId: payload.blockId });
      const operation = { operation: ApiPatchOperation.UpdateSummaryBlock } as const;
      const validationResult = summaryBlockUpdateSchema.safeParse({ ...payload, ...operation });

      if (!validationResult.success) {
        toast.error('Unable to update summary block');
        throw new Error('Validation failed');
      }

      return patchEndorsementSummary({ ...validationResult.data, ...operation });
    },
    onSuccess: async (_, variables) => {
      await reloadSubmission();
      highlightChangedBlock(variables.blockId);
    },
    onSettled: () => useEntityStore.setState({ loadingBlockId: undefined }),
  });

  const hideBlockFromPreview = useMutation({
    mutationFn: (payload: DropApiOperation<SummaryBlockHideFromPreview>) => {
      useEntityStore.setState({ loadingBlockId: payload.blockId });
      return patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.HideBlockFromPreview });
    },
    onSuccess: async (_, variables) => {
      await reloadSubmission();
      highlightChangedBlock(variables.blockId);
    },
    onSettled: () => useEntityStore.setState({ loadingBlockId: undefined }),
  });

  const showBlockInPreview = useMutation({
    mutationFn: (payload: DropApiOperation<SummaryBlockShowInPreview>) => {
      useEntityStore.setState({ loadingBlockId: payload.blockId });
      return patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.ShowBlockInPreview });
    },
    onSuccess: async (_, variables) => {
      await reloadSubmission();
      highlightChangedBlock(variables.blockId);
    },
    onSettled: () => useEntityStore.setState({ loadingBlockId: undefined }),
  });

  const restoreAllBlocksHiddenFromPreview = useMutation({
    mutationFn: () => patchEndorsementSummary({ operation: ApiPatchOperation.RestoreBlocksHiddenFromPreview }),
    onMutate: () => useEntityStore.setState({ isLoading: true }),
    onSuccess: reloadSubmission,
    onSettled: () => useEntityStore.setState({ isLoading: false }),
  });

  const restoreEndorsementSummaryTemplate = useMutation({
    mutationFn: (templateId: number) => {
      const endorsement = useEntityStore.getState().submission;

      if (!isEndorsementData(endorsement)) {
        console.error('Unable to restore endorsement template outside of an endorsement');
        throw new Error('Not in endorsement context');
      }

      return patchEntity({
        operation: ApiPatchOperation.RestoreEndorsementSummaryTemplate,
        endorsement_summary_template_id: templateId,
      });
    },
    onMutate: () => useEntityStore.setState({ isLoading: true }),
    onSuccess: reloadSubmission,
    onSettled: () => useEntityStore.setState({ isLoading: false }),
  });

  const reorderSummaryBlock = useMutation({
    mutationFn: (payload: DropApiOperation<SummaryBlockReorder>) => {
      const submission = useEntityStore.getState().submission;
      const summarySections: any[] = get(submission, 'summary.sections', []);

      if (!isEndorsementData(submission)) {
        throw new Error('Cannot reorder summary section outside of endorsement context.');
      }

      const blockSection = summarySections.find((s) => s.blocks?.some((b: any) => b.id === payload.blockId));
      const isCrossSection = !!payload.targetSectionId && payload.targetSectionId !== blockSection?.id;

      if (submission && blockSection) {
        const block = blockSection.blocks?.find((b: any) => b.id === payload.blockId);
        let updatedSections = [];

        if (isCrossSection) {
          updatedSections = summarySections.map((s: any) => {
            if (s.id === blockSection.id) {
              return {
                ...s,
                layout: reorderLayout(s.layout, payload.blockId, -1),
                blocks: s.blocks?.filter((b: any) => b.id !== payload.blockId),
              };
            }
            if (s.id === payload.targetSectionId) {
              return {
                ...s,
                layout: reorderLayout(s.layout, payload.blockId, payload.order),
                blocks: [...(s.blocks ?? []), { ...block, section_id: payload.targetSectionId }],
              };
            }
            return s;
          });
        } else {
          updatedSections = summarySections.map((s) =>
            s.id === blockSection.id ? { ...s, layout: reorderLayout(s.layout, payload.blockId, payload.order) } : s,
          );
        }

        const updatedSubmission = {
          ...submission,
          summary: {
            ...submission.summary,
            sections: updatedSections,
          },
        };

        useEntityStore.setState({ submission: updatedSubmission });
      }

      return patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.ReorderSummaryBlock });
    },
    onMutate: () => useEntityStore.setState({ isLoading: true }),
    onSuccess: reloadSubmission,
    onError: reloadSubmission,
    onSettled: () => useEntityStore.setState({ isLoading: false }),
  });

  const reorderSummarySection = useMutation({
    mutationFn: (payload: DropApiOperation<SummarySectionReorder>) => {
      const submission = useEntityStore.getState().submission;
      const summarySections: any[] = get(submission, 'summary.sections', []);
      const currentLayout: string[] = get(submission, 'summary.layout', []);

      if (!isEndorsementData(submission)) {
        throw new Error('Cannot reorder summary section outside of endorsement context.');
      }

      if (submission && currentLayout.length > 0) {
        const changeSectionIds = new Set(summarySections.filter((s) => !isSummarySection(s)).map((s) => s.id));
        const isMovingChangesGroup = payload.sectionId === ENDORSEMENT_DETAILS_SENTINEL;
        const changeIdsInOrder = currentLayout.filter((id) => changeSectionIds.has(id));

        const layoutWithoutActive = isMovingChangesGroup
          ? currentLayout.filter((id) => !changeSectionIds.has(id) && id !== ENDORSEMENT_DETAILS_SENTINEL)
          : currentLayout.filter((id) => id !== payload.sectionId);

        const findIndex = (): number => {
          if (payload.insertBeforeId === ENDORSEMENT_DETAILS_SENTINEL) {
            const firstChangeIdx = layoutWithoutActive.findIndex((id) => changeSectionIds.has(id));
            return firstChangeIdx !== -1 ? firstChangeIdx : layoutWithoutActive.indexOf(ENDORSEMENT_DETAILS_SENTINEL);
          }
          if (payload.insertBeforeId) {
            return layoutWithoutActive.indexOf(payload.insertBeforeId);
          }
          return -1;
        };

        const idx = findIndex();
        const destinationIndex = idx === -1 ? layoutWithoutActive.length : idx;

        const movingIds = changeIdsInOrder.length > 0 ? changeIdsInOrder : [ENDORSEMENT_DETAILS_SENTINEL];
        const optimisticLayout = isMovingChangesGroup
          ? [
              ...layoutWithoutActive.slice(0, destinationIndex),
              ...movingIds,
              ...layoutWithoutActive.slice(destinationIndex),
            ]
          : reorderLayout(currentLayout, payload.sectionId, destinationIndex);

        const updatedSubmission = {
          ...submission,
          summary: {
            ...submission.summary,
            layout: optimisticLayout,
          },
        };

        useEntityStore.setState({ submission: updatedSubmission });
      }

      return patchEndorsementSummary({ ...payload, operation: ApiPatchOperation.ReorderSummarySection });
    },
    onMutate: () => useEntityStore.setState({ isLoading: true }),
    onSuccess: reloadSubmission,
    onError: reloadSubmission,
    onSettled: () => useEntityStore.setState({ isLoading: false }),
  });

  return {
    createSummaryBlock,
    deleteSummaryBlock,
    deleteSummarySection,
    updateSummaryBlock,
    hideBlockFromPreview,
    showBlockInPreview,
    restoreAllBlocksHiddenFromPreview,
    restoreEndorsementSummaryTemplate,
    reorderSummaryBlock,
    reorderSummarySection,
  };
}
